import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";

const C = { espresso: "#280B03", cream: "#FBF8F5", terracotta: "#985E4B", tan: "#C2AA9D", maroon: "#53271E" };
const LOGO = "https://unlayer.production.bsport.io/1793085117040-upload-wk44-logo-petal.png";
const PHOTOS = {
  studio: "https://unlayer.production.bsport.io/1793085104194-upload-wk44-foto-studio.jpg",
  balring: "https://unlayer.production.bsport.io/1793085101398-upload-wk44-foto-bal-ring.jpg",
  licht: "https://unlayer.production.bsport.io/1793085103004-upload-wk44-foto-licht.jpg",
};

const MEMBERSHIPS = [
  { name: "Classic", price: "from €99 / 4 weeks", detail: "1× per week · 4 credits / 4 weeks", popular: false,
    y12: "https://backoffice.bsport.io/checkout/6040/subscription/53960?force=true",
    m6: "https://backoffice.bsport.io/checkout/6040/subscription/51688?force=true" },
  { name: "Signature", price: "from €179 / 4 weeks", detail: "2× per week · 8 credits / 4 weeks", popular: true,
    y12: "https://backoffice.bsport.io/checkout/6040/subscription/53961?force=true",
    m6: "https://backoffice.bsport.io/checkout/6040/subscription/51689?force=true" },
  { name: "Elite", price: "from €229 / 4 weeks", detail: "3× per week · 12 credits / 4 weeks", popular: false,
    y12: "https://backoffice.bsport.io/checkout/6040/subscription/53962?force=true",
    m6: "https://backoffice.bsport.io/checkout/6040/subscription/51690?force=true" },
];

const MAILS = {
  "mail-1-en": {
    photo: PHOTOS.studio,
    eyebrow: "Your first class is done",
    heading: "How did your first class feel?",
    paragraphs: [
      "Hello,",
      "How lovely to have had you on the reformer. A first class often takes a little getting used to: new springs, new terminology, muscles you had quite forgotten you owned. That is perfectly natural.",
      "One tip for next time: arrive five minutes early, and your instructor will set up the reformer just for you.",
      "Book your second class within a week, and you will carry today's feeling straight on. See you soon in the studio.",
    ],
    button: { label: "Book your second class →", href: "https://www.maisoncore.nl/schedule" },
  },
  "mail-2-en": {
    photo: PHOTOS.balring,
    eyebrow: "Two classes in",
    heading: "Your body remembers more than you think",
    paragraphs: [
      "Hello,",
      "Two classes in. You may already notice it: you stand a little taller, and your core feels stronger.",
      "Reformer Pilates works best in rhythm; those who come two or three times a week see a real difference in strength and posture within a month. That is why our memberships exist: Classic for a steady rhythm, Signature for those who want more, Elite for unlimited classes and extras.",
      "Your final introductory class is still waiting: book it and discover what consistency can do.",
    ],
    button: { label: "Book your third class →", href: "https://www.maisoncore.nl/schedule" },
    smallLink: { label: "Enjoyed it? A short review means the world to us. →", href: "https://g.page/r/CWYypd3JRiGhEBE/review" },
  },
  "mail-3-en": {
    photo: PHOTOS.licht,
    eyebrow: "Your introduction is complete",
    heading: "Your €59 counts",
    paragraphs: [
      "Hello,",
      "You have completed your introduction, beautifully done. Now the real work begins, and the loveliest part.",
      "Choose a membership within 7 days and we will credit the €59 of your introduction in full against your first month.",
      "Not sure which membership suits you? Reply to our email and we will happily help you choose.",
    ],
    code: "INTRO59START",
    memberships: true,
    button: { label: "View the memberships →", href: "https://www.maisoncore.nl/pricing" },
  },
  "mail-4-en": {
    photo: PHOTOS.studio,
    eyebrow: "We are keeping your place warm",
    heading: "One more time: your €59 counts",
    paragraphs: [
      "Hello,",
      "It has been a little while since we last saw you, and we understand.",
      "Because we would love to welcome you back, we are extending your offer once: choose a membership within 7 days with the code INTRO59TERUG and we will still credit the €59 of your introduction against your first month.",
      "Your reformer is ready and waiting.",
    ],
    code: "INTRO59TERUG",
    memberships: true,
    button: { label: "Choose your membership →", href: "https://www.maisoncore.nl/pricing" },
  },
};

function useNoIndex(title) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, [title]);
}

const eyebrowStyle = {
  fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif",
  fontSize: 13, letterSpacing: 3, textTransform: "uppercase", color: C.terracotta,
};
const bodyStyle = { fontFamily: "Arial, Helvetica, sans-serif", fontSize: 16, lineHeight: "26px", color: C.espresso };
const btnStyle = {
  display: "inline-block", background: C.terracotta, color: C.cream, padding: "15px 30px",
  fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif", fontSize: 15,
  letterSpacing: 2, textTransform: "uppercase", textDecoration: "none",
};

function MembershipCard({ m }) {
  return (
    <div style={{ border: `1px solid ${C.tan}`, background: C.cream, padding: "24px 24px 20px" }} data-testid={`intro-membership-${m.name.toLowerCase()}`}>
      {m.popular && <p style={{ ...eyebrowStyle, margin: "0 0 8px" }}>★ Most popular</p>}
      <p className="font-serif" style={{ margin: 0, fontSize: 30, color: C.espresso }}>{m.name}</p>
      <p style={{ ...bodyStyle, margin: "6px 0 2px", fontWeight: "bold" }}>{m.price}</p>
      <p style={{ ...bodyStyle, margin: "0 0 16px", color: C.terracotta, fontSize: 14 }}>{m.detail}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <a href={m.y12} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle, padding: "12px 18px", fontSize: 13 }}>Start · 12 months</a>
        <a href={m.m6} target="_blank" rel="noopener noreferrer" style={{ ...btnStyle, padding: "12px 18px", fontSize: 13, background: "transparent", color: C.terracotta, border: `1px solid ${C.terracotta}` }}>6 months</a>
      </div>
    </div>
  );
}

export default function IntroMail() {
  const { slug } = useParams();
  const mail = MAILS[slug];
  useNoIndex(mail ? `Maison Core — ${mail.heading}` : "Maison Core");
  if (!mail) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen px-3 py-8 sm:px-6 sm:py-14" style={{ background: C.tan }} data-testid={`intro-${slug}`}>
      <div className="mx-auto w-full shadow-lg" style={{ maxWidth: 600, background: C.cream }}>
        {/* Espresso banner with logo */}
        <div style={{ background: C.espresso, padding: "36px 40px", textAlign: "center" }}>
          <a href="https://www.maisoncore.nl/" data-testid="intro-logo-link">
            <img src={LOGO} alt="Maison Core. Reformer Studio" style={{ width: 200, maxWidth: "100%", height: "auto", display: "inline-block" }} />
          </a>
        </div>
        {/* Studio photo */}
        <img src={mail.photo} alt="Maison Core studio" style={{ display: "block", width: "100%", height: "auto" }} />
        {/* Content */}
        <div style={{ padding: "36px 40px 8px" }} className="!px-6 sm:!px-10">
          <p style={{ ...eyebrowStyle, margin: "0 0 10px" }} data-testid="intro-eyebrow">{mail.eyebrow}</p>
          <h1 className="font-serif" style={{ margin: "0 0 20px", fontSize: 38, lineHeight: 1.1, color: C.espresso }} data-testid="intro-heading">{mail.heading}</h1>
          {mail.paragraphs.map((p, i) => (
            <p key={i} style={{ ...bodyStyle, margin: "0 0 16px" }}>{p}</p>
          ))}
          {mail.code && (
            <div style={{ border: `1px dashed ${C.terracotta}`, padding: "14px 18px", margin: "4px 0 20px", textAlign: "center" }} data-testid="intro-code">
              <span style={{ ...eyebrowStyle, fontSize: 12 }}>Use the code at checkout</span>
              <div className="font-serif" style={{ fontSize: 26, color: C.espresso, marginTop: 4 }}>{mail.code}</div>
            </div>
          )}
          {mail.memberships && (
            <div style={{ display: "grid", gap: 14, margin: "8px 0 24px" }}>
              {MEMBERSHIPS.map((m) => <MembershipCard key={m.name} m={m} />)}
            </div>
          )}
          <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
            <a href={mail.button.href} style={btnStyle} data-testid="intro-cta">{mail.button.label}</a>
          </div>
          {mail.smallLink && (
            <p style={{ ...bodyStyle, fontSize: 14, textAlign: "center", margin: "16px 0 0" }}>
              <a href={mail.smallLink.href} target="_blank" rel="noopener noreferrer" style={{ color: C.terracotta, textDecoration: "underline" }} data-testid="intro-review-link">{mail.smallLink.label}</a>
            </p>
          )}
          <p style={{ ...eyebrowStyle, textAlign: "center", margin: "36px 0 28px" }}>The Studio · Buitenveldertselaan 120 · Amsterdam</p>
        </div>
        {/* Maroon footer */}
        <div style={{ background: C.maroon, padding: "40px 40px 44px", textAlign: "center" }} data-testid="intro-footer">
          <p style={{ ...bodyStyle, color: C.cream, margin: "0 0 4px" }}>Until next time,</p>
          <a href="https://www.maisoncore.nl/">
            <img src={LOGO} alt="Maison Core" style={{ width: 170, maxWidth: "100%", height: "auto", display: "inline-block", margin: "10px 0 14px" }} />
          </a>
          <p style={{ ...eyebrowStyle, color: C.tan, margin: "0 0 18px" }}>Buitenveldertselaan 120, 1081 AB Amsterdam</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 18 }}>
            <a href="https://instagram.com/maisoncore_" target="_blank" rel="noopener noreferrer" aria-label="Instagram" style={{ color: C.cream }} data-testid="intro-instagram">
              <Instagram size={20} />
            </a>
            <a href="mailto:info@maisoncore.nl" aria-label="Email" style={{ color: C.cream }} data-testid="intro-email">
              <Mail size={20} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
